import type * as THREE from 'three/webgpu';
import { isFeatureFace, isFeatureSurface, type FeatureFace } from '../../mesh/feature';

/** 法向/方向平行判定容差。 */
const MEASURE_PLANE_PARALLEL_EPSILON = 1e-6;

/**
 * 判断特征面是否为模型上的有边界平面面。
 *
 * 设计说明：
 * 1. 基础 {@link FeatureFace} 在拓扑语义上代表平面面；
 * 2. {@link FeatureSurface} 代表圆柱/球面等非平面解析曲面；
 * 3. 基准面不走 FeatureFace，由 {@link MeasureDistancePrimitiveKind.Plane} 单独表达无限延伸语义。
 *
 * @param face 面代理。
 * @returns 是否为有边界平面面。
 */
export function isBoundedPlanarFeatureFace(face: FeatureFace): boolean {
    return isFeatureFace(face) && !isFeatureSurface(face);
}

/**
 * 读取有边界平面面的世界平面参数。
 *
 * @param face 面代理。
 * @returns 世界平面参数；非平面面时返回 null。
 */
export function resolveBoundedPlanarFaceWorldPlane(
    face: FeatureFace,
): { point: THREE.Vector3; normal: THREE.Vector3 } | null {
    if (!isBoundedPlanarFeatureFace(face)) {
        return null;
    }
    const worldPlane = face.getWorldPlane();
    return {
        point: worldPlane.point.clone(),
        normal: worldPlane.normal.clone().normalize(),
    };
}

/**
 * 判断两单位法向是否平行。
 *
 * @param normalA 法向 A。
 * @param normalB 法向 B。
 * @returns 是否平行。
 */
export function areMeasureNormalsParallel(
    normalA: THREE.Vector3,
    normalB: THREE.Vector3,
): boolean {
    return Math.abs(normalA.dot(normalB)) > 1 - MEASURE_PLANE_PARALLEL_EPSILON;
}

/**
 * 判断方向是否与平面法向正交（即方向平行于平面）。
 *
 * @param direction 待测方向。
 * @param planeNormal 平面单位法向。
 * @returns 是否平行于平面。
 */
export function isMeasureDirectionParallelToPlane(
    direction: THREE.Vector3,
    planeNormal: THREE.Vector3,
): boolean {
    if (direction.lengthSq() <= MEASURE_PLANE_PARALLEL_EPSILON) {
        return false;
    }
    const normalizedDirection = direction.clone().normalize();
    return Math.abs(normalizedDirection.dot(planeNormal)) <= MEASURE_PLANE_PARALLEL_EPSILON;
}
